import { Component, Input, OnInit } from '@angular/core';
import { UntypedFormGroup, Validators } from '@angular/forms';
import _ from 'lodash';
import { AllDropDownOptions, AuthorComponentService, AuthorDisplayValueType, AuthorDropdownOption, AuthorType } from '../../services/author-component.service';
import { PageLoaderService } from '../../services/page-loader.service';
import { SharedService } from '../../services/shared-service';
import { AuthorComponentData } from '../../types/author-component-data.type';
import { DropDownOption } from '../../types/dropdown-option.type';


@Component({
    selector: 'app-modern-author',
    templateUrl: './modern-author.component.html',
    styleUrls: ['./modern-author.component.scss'],
    standalone: false
})
export class ModernAuthorComponent implements OnInit {

    @Input() authorFormGroup: UntypedFormGroup;
    @Input("leave-id") leaveId: number = null;

    private authorData: AuthorComponentData = null;

    usersList: DropDownOption<number>[] = [];
    doctorsList: DropDownOption<number>[] = [];
    coordinatorsList: DropDownOption<number>[] = [];

    authorTypesList: AuthorDropdownOption[] = [];
    dropDownActivate = {
        user: false,
        coordinador: false,
        doctor: false
    };


    constructor(
        private authorComponentService: AuthorComponentService,
        private sharedService: SharedService,
        private pageLoader: PageLoaderService,
    ) { }


    ngOnInit(): void {
        this.init();
    }


    //#region    ***************************  INITIALIZATION  ***************************

    /**
     * Initialize default data
     */
    private async init(): Promise<void> {
        const companyId = parseInt(localStorage.getItem('Empresa'));
        this.authorData = await this.getAuthorComponentData(this.leaveId, companyId);
        const loggedUserAuthorType = this.getLoggedUserAuthorType(this.authorData);

        this.authorTypesList = this.authorComponentService.getAuthorTypesList(
            loggedUserAuthorType,
            this.authorData.bajaMedicoId != null,
            this.authorData.empresaAdministrativa != null,
            this.authorData.empresaCoordinator != null
        );

        // get dropdown options
        const lists = await this.getAllDropdownOptions();
        this.usersList = lists.users;
        this.doctorsList = lists.doctors;
        this.coordinatorsList = lists.coordinators;

    }


    /**
     * Get all dropdown options
     */
    private async getAllDropdownOptions(): Promise<AllDropDownOptions> {
        setTimeout(() => this.pageLoader.ShowPageLoader(), 0);

        let lists: AllDropDownOptions = null;

        try {
            lists = await this.authorComponentService.getDropdownOptions();
        } catch (error) {
            console.error(error);
            this.sharedService.ShowSnackBar('Lo siento, no pude obtener los datos de los autores');
        }

        setTimeout(() => this.pageLoader.HidePageLoader(), 0);
        return lists;
    }


    /**
     * Get logged user author type
     */
    private getLoggedUserAuthorType(data: AuthorComponentData): AuthorType {
        if (data.usuarioCoordinador)
            return "C";
        else if (data.usuarioMedicoId)
            return "M";
        else
            return "G";
    }


    /**
     * Get author component data
     */
    private async getAuthorComponentData(leaveId: number, companyId: number): Promise<AuthorComponentData> {
        setTimeout(() => this.pageLoader.ShowPageLoader(), 0);
        let result: AuthorComponentData = null;

        try {
            const response = await this.authorComponentService.getData(leaveId, companyId).toPromise();
            if (response.code === 0)
                result = response.result;
            else {
                console.error(response.message ?? response);
                this.sharedService.ShowSnackBar('Lo siento, ha fallado la obtención de los datos del autor');
            }
        } catch (error) {
            console.error(error);
            this.sharedService.ShowSnackBar('Lo siento, ha fallado la obtención de los datos del autor');
        }

        setTimeout(() => this.pageLoader.HidePageLoader(), 0);
        return result;
    }

    //#endregion


    //#region    ***************************  EVENTS  *************************** 

    /**
     * Listen doctor dropdown change
     */
    onDoctorChange(event: { value: number; }): void {
        this.setDoctorAsAuthor(event.value);
    }


    /**
     * Listen user dropdown change
     */
    onUserChange(event: { value: number; }): void {
        const userId = event.value;
        const userAbrev = this.usersList.find(x => x.value === userId).stringValue;
        this.setFormValues(userAbrev, null);
    }


    /**
     * Listen coordinator dropdown change
     */
    onCoordinatorChange(event: { source: { selected: { viewValue: string; }; }; }): void {
        this.setFormValues(event.source.selected.viewValue, null);
    }


    /**
     * Listen author type changes
     */
    onAuthorTypeChange(event: { source: { selected: { viewValue: AuthorDisplayValueType; }; }; value: AuthorType; }): void {
        this.resetFieldsOnAuthorTypeChange();
        const viewValue = event.source.selected.viewValue;

        switch (viewValue) {
            case "Yo":
                this.changeDropdownVisibility(false, false, false);
                this.setFormValuesForCurrentUser(event.value);
                break;

            case "Médico asignado":
                this.changeDropdownVisibility(false, false, false);
                this.setDoctorAsAuthor(this.authorData.bajaMedicoId);
                break;

            case "Administrativo asignado":
                this.changeDropdownVisibility(false, false, false);
                this.setFormValues(this.authorData.empresaAdministrativa, null);
                break;

            case "Coordinador asignado":
                this.changeDropdownVisibility(false, false, false);
                this.setFormValues(this.authorData.empresaCoordinator, null);
                break;

            case "Otro coordinador":
                this.changeDropdownVisibility(false, true, false);
                break;

            case "Otro médico":
                this.changeDropdownVisibility(false, false, true);
                break;

            case "Otro usuario":
                this.changeDropdownVisibility(true, false, false);
                break;

            case "La empresa":
                this.changeDropdownVisibility(false, false, false);
                this.setFormValues("EMPRESA", null);
                break;

            case "La mutua":
                this.changeDropdownVisibility(false, false, false);
                this.setFormValues("MUTUA", null);
                break;
        }
    }

    //#endregion


    //#region   ***************************  MECHANISMS  ***************************

    /**
     * Change dropdowns visibility and validators
     */
    private changeDropdownVisibility(showUsers: boolean, showCoordinators: boolean, showDoctors: boolean): void {
        this.dropDownActivate.user = showUsers;
        this.dropDownActivate.coordinador = showCoordinators;
        this.dropDownActivate.doctor = showDoctors;

        if (showUsers) {
            this.authorFormGroup.get('Usuario').setValidators([Validators.required]);
            this.authorFormGroup.get('Usuario').updateValueAndValidity();
        } else if (showCoordinators) {
            this.authorFormGroup.get('Coordinator').setValidators([Validators.required]);
            this.authorFormGroup.get('Coordinator').updateValueAndValidity();
        } else if (showDoctors) {
            this.authorFormGroup.get('Medico').setValidators([Validators.required]);
            this.authorFormGroup.get('Medico').updateValueAndValidity();
        }

    }


    /**
     * Set form values for current user
     */
    private setFormValuesForCurrentUser(authorType: AuthorType): void {
        switch (authorType) {
            case "M":
                const doctorId = this.authorData.usuarioMedicoId;
                const doctorName = this.doctorsList.find(x => x.value === doctorId).viewValue;
                this.setFormValues(doctorName, doctorId);
                break;

            case "C":
                this.setFormValues(this.authorData.usuarioCoordinador, null);

            case "G":
                this.setFormValues(this.authorData.usuarioAbrevacion, null);
        }
    }


    /**
     * Set author name and doctor ID
     */
    private setFormValues(authorName: string, doctorId: number): void {
        this.authorFormGroup.get('Nombre').setValue(authorName);
        this.authorFormGroup.get('MedicoId').setValue(doctorId);
    }


    /**
     * Reset fields on author type change
     */
    private resetFieldsOnAuthorTypeChange(): void {
        // set all values other than type to null
        this.authorFormGroup.patchValue({
            Usuario: null,
            Medico: null,
            Coordinator: null,
            Nombre: null,
            MedicoId: null,
        });

        // remove validators
        this.authorFormGroup.get('Usuario').clearValidators();
        this.authorFormGroup.get('Coordinator').clearValidators();
        this.authorFormGroup.get('Medico').clearValidators();
        this.authorFormGroup.updateValueAndValidity();
    }


    /**
     * Set given doctor as author
     */
    private setDoctorAsAuthor(doctorId: number) {
        const doctorName = this.doctorsList.find(x => x.value === doctorId).viewValue;
        this.setFormValues(doctorName, doctorId);
    }

    //#endregion

}
